import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Button, Card, Grid, Stack, Chip, 
  ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, Accordion, 
  AccordionSummary, AccordionDetails, CircularProgress, Alert
} from '@mui/material';
import { 
  CheckCircle, Cancel, Star, WorkspacePremium, Groups, 
  ExpandMore, RocketLaunch, Phone, Security, People,
  LightbulbCircle
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

export default function Pricing() {
  const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
  const darkBg = '#070B14';
  const lightBg = '#f8fafc';

  // State Management
  const [billing, setBilling] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState('faq0');

  const handleBillingChange = (event, newBilling) => {
    if (newBilling !== null) {
      setBilling(newBilling);
    }
  };

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  // Fetch Dynamic Plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/subscription/plans?frequency=${billing}`);
        const data = await response.json();
        
        if (data.status === 'success' && data.data) {
          setPlans(data.data);
        } else {
          setPlans([]);
        }
      } catch (err) {
        console.error('Error loading plans:', err);
        setError('Unable to load subscription plans. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [billing]);

  // Handle Subscription Click
  const subscribeToPlan = async (planId, planName) => {
    try {
      const response = await fetch('/api/session-status');
      const sessionData = await response.json();

      if (!sessionData.hasAccessToken) {
        window.location.href = `/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
        return;
      }

      const customerDetails = {
        name: sessionData.user?.name || 'User',
        email: sessionData.user?.email || '',
        phone: sessionData.user?.mobile || ''
      };

      const subscriptionResponse = await fetch('/api/subscription/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: planId,
          customer_details: customerDetails
        })
      });

      const subscriptionData = await subscriptionResponse.json();

      if (subscriptionData.status === 'success') {
        if (subscriptionData.data.short_url) {
          window.location.href = subscriptionData.data.short_url;
        } else {
          alert('Subscription created successfully! Please check your profile for payment details.');
          window.location.href = '/my-profile';
        }
      } else {
        alert(subscriptionData.message || 'Failed to create subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error subscribing to plan:', error);
      alert('Error creating subscription. Please try again.');
    }
  };

  return (
    <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pb: 10 }}>
      
      {/* 🌟 HEADER SECTION 🌟 */}
      <Box sx={{ pt: { xs: 12, md: 16 }, pb: { xs: 6, md: 8 }, bgcolor: darkBg, textAlign: 'center', position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '50%', height: '2px', background: primaryGradient, boxShadow: '0 0 20px 5px rgba(255, 81, 47, 0.4)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" fontWeight="900" color="white" sx={{ mb: 2, letterSpacing: '-1px', fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Pricing Plans
          </Typography>
          <Typography variant="h6" color="#94A3B8" fontWeight="500">
            अपनी राजनीतिक अभियान आवश्यकताओं के लिए सही योजना चुनें
          </Typography>
        </Container>
      </Box>

      {/* 🌟 DYNAMIC SUBSCRIPTION PLANS 🌟 */}
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: 'white', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 10px 40px rgba(0,0,0,0.03)' }}>
          
          <Box textAlign="center" mb={5}>
            <Typography variant="h4" fontWeight="900" color="#1E293B" mb={1}>सब्स्क्रिप्शन योजनाएं</Typography>
            <Typography variant="subtitle1" color="#64748B" mb={4}>Flexible plans with profile bundles</Typography>
            
            <ToggleButtonGroup
              color="primary"
              value={billing}
              exclusive
              onChange={handleBillingChange}
              sx={{ 
                bgcolor: '#f1f5f9', p: 0.5, borderRadius: '50px',
                '& .MuiToggleButton-root': { border: 'none', borderRadius: '50px !important', px: 4, py: 1, fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', color: '#64748B' },
                '& .Mui-selected': { bgcolor: 'white !important', color: '#1E293B !important', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }
              }}
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="yearly">
                Yearly <Chip label="Save 25%" size="small" sx={{ ml: 1, bgcolor: '#10b981', color: 'white', fontWeight: 'bold', height: 20 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {loading ? (
            <Box textAlign="center" py={8}>
              <CircularProgress sx={{ color: '#FF6A00' }} />
              <Typography mt={2} color="text.secondary">Loading amazing plans for you...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto', borderRadius: 3 }}>{error}</Alert>
          ) : plans.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary">No plans available right now. Please check back later.</Typography>
            </Box>
          ) : (
            <Grid container spacing={4} justifyContent="center">
              {plans.map((plan, index) => (
                // 👇 FIX: Added mt: { xs: 3, md: 0 } so the Most Popular badge doesn't get cut off on mobile
                <Grid item xs={12} md={6} lg={4} key={plan.id || index} sx={{ mt: plan.is_popular ? { xs: 3, md: 0 } : 0 }}>
                  <Card 
                    sx={{ 
                      height: '100%', p: 4, borderRadius: 4, display: 'flex', flexDirection: 'column',
                      border: plan.is_popular ? '2px solid #FF6A00' : '1px solid #e2e8f0',
                      boxShadow: plan.is_popular ? '0 15px 35px rgba(255, 106, 0, 0.15)' : 'none',
                      position: 'relative', overflow: 'visible', // 👇 FIX: overflow visible is crucial for badges
                      transition: 'transform 0.3s',
                      '&:hover': { transform: 'translateY(-8px)' }
                    }}
                  >
                    {plan.is_popular && (
                      <Chip 
                        label="MOST POPULAR" 
                        icon={<Star style={{ color: 'white' }} fontSize="small" />}
                        sx={{ position: 'absolute', top: -15, left: '50%', transform: 'translateX(-50%)', background: primaryGradient, color: 'white', fontWeight: 'bold', boxShadow: '0 4px 10px rgba(238,9,121,0.3)' }} 
                      />
                    )}
                    
                    <Typography variant="h5" fontWeight="800" color="#1E293B" mb={1} textAlign="center">
                      {plan.name}
                    </Typography>
                    
                    <Box textAlign="center" mb={3}>
                      <Typography variant="h3" fontWeight="900" color={plan.is_popular ? '#FF6A00' : '#1E293B'} display="inline">
                        ₹{plan.price}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary" display="inline"> /{plan.frequency_label}</Typography>
                      
                      {plan.trial_period_days && (
                        <Box mt={1}><Chip label={`${plan.trial_period_days}-day free trial`} size="small" color="info" /></Box>
                      )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                      {plan.description}
                    </Typography>

                    <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, mb: 3, textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight="700" color="#1E293B">Max Profiles: <span style={{ color: '#FF6A00' }}>{plan.max_profiles}</span></Typography>
                    </Box>

                    <Box flexGrow={1}>
                      {plan.features?.map((feature, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                          <CheckCircle sx={{ color: '#10b981', fontSize: 20, mr: 1.5, mt: 0.2 }} />
                          <Typography variant="body2" color="#475569">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button 
                      fullWidth 
                      variant={plan.is_popular ? "contained" : "outlined"}
                      onClick={() => subscribeToPlan(plan.id, plan.name)}
                      sx={{ 
                        mt: 4, py: 1.5, borderRadius: '50px', fontWeight: 'bold', fontSize: '1rem',
                        background: plan.is_popular ? primaryGradient : 'transparent',
                        color: plan.is_popular ? 'white' : '#1E293B',
                        borderColor: '#e2e8f0',
                        '&:hover': { background: plan.is_popular ? primaryGradient : '#f1f5f9' }
                      }}
                    >
                      Choose {plan.name}
                    </Button>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Container>

      {/* 🌟 SPECIAL BUNDLE PRICING 🌟 */}
      <Container maxWidth="lg" sx={{ mt: 10, mb: 10 }}>
        <Box textAlign="center" mb={5}>
          <Typography variant="h4" fontWeight="900" color="#1E293B" mb={1}>विशेष बंडल मूल्य</Typography>
          <Typography variant="subtitle1" color="#64748B">Special Bundle Pricing for Panchayati Raj officials</Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 4, borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
              <Box textAlign="center" mb={4}>
                <Groups sx={{ fontSize: 50, color: '#3b82f6', mb: 1 }} />
                <Typography variant="h5" fontWeight="800">Plan 1 Details</Typography>
              </Box>
              
              <Box sx={{ bgcolor: '#eff6ff', p: 3, borderRadius: 3, mb: 4 }}>
                <Typography variant="h6" fontWeight="800" color="#2563eb" mb={2}>3 Profiles</Typography>
                <Typography variant="subtitle2" fontWeight="700" mb={1}>Option 1:</Typography>
                <Typography variant="body2" mb={2} color="text.secondary">• One Hindi • One English • One other</Typography>
                
                <Typography variant="subtitle2" fontWeight="700" mb={1}>OR Option 2:</Typography>
                <Typography variant="body2" color="text.secondary">• One for yourself • Share with friends • Share with family</Typography>
              </Box>

              <Typography variant="subtitle2" fontWeight="700" mb={2}>Included Features:</Typography>
              {['AI-powered designs', '500+ templates', 'HD downloads', 'Video maker', '24/7 support'].map((feat, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle sx={{ color: '#10b981', fontSize: 18, mr: 1 }} />
                  <Typography variant="body2" color="#475569">{feat}</Typography>
                </Box>
              ))}
            </Card>
          </Grid>

          {/* 👇 FIX: Added mt: { xs: 3, md: 0 } for Most Popular badge space */}
          <Grid item xs={12} md={5} sx={{ mt: { xs: 3, md: 0 } }}>
            {/* 👇 FIX: overflow: 'visible' ensures the badge shows fully */}
            <Card sx={{ p: 4, borderRadius: 4, height: '100%', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: '2px solid #f59e0b', position: 'relative', overflow: 'visible' }}>
              <Chip label="Most Popular" color="warning" sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', fontWeight: 'bold' }} />
              
              <Box textAlign="center" mb={4} mt={1}>
                <WorkspacePremium sx={{ fontSize: 50, color: '#f59e0b', mb: 1 }} />
                <Typography variant="h5" fontWeight="800">Plan 2 Details</Typography>
              </Box>
              
              <Box sx={{ bgcolor: '#fffbeb', p: 3, borderRadius: 3, mb: 4 }}>
                <Typography variant="h6" fontWeight="800" color="#d97706" mb={2}>5 Profiles</Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>• Multiple languages</Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>• Share with team, family & friends</Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>• Different positions support</Typography>
                <Typography variant="body2" color="text.secondary">• Annual discount applied</Typography>
              </Box>

              <Typography variant="subtitle2" fontWeight="700" mb={2}>All Plan 1 features + :</Typography>
              {['Priority support', 'More premium templates', 'Custom designs', 'Bulk downloads'].map((feat, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle sx={{ color: '#f59e0b', fontSize: 18, mr: 1 }} />
                  <Typography variant="body2" color="#475569">{feat}</Typography>
                </Box>
              ))}
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* 🌟 FEATURE COMPARISON TABLE 🌟 */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" fontWeight="900" color="#1E293B" mb={1}>Feature Comparison</Typography>
          <Typography variant="subtitle1" color="#64748B">Compare all features across our plans</Typography>
        </Box>
        
        {/* 👇 FIX: Helper text for mobile users to know they can scroll */}
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'right', mb: 1 }}>
          Swipe horizontally to see more ➔
        </Typography>

        {/* 👇 FIX: overflowX: 'auto' is crucial here for horizontal scrolling on mobile */}
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 650 }} aria-label="pricing table">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: '800', color: '#1E293B', py: 3 }}>Features</TableCell>
                <TableCell align="center" sx={{ fontWeight: '800', color: '#64748B' }}>Basic</TableCell>
                <TableCell align="center" sx={{ fontWeight: '800', color: '#FF6A00' }}>Local Leader</TableCell>
                <TableCell align="center" sx={{ fontWeight: '800', color: '#dc2626' }}>Party Member</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { name: 'Image Design Templates (no photo)', basic: true, local: true, party: true },
                { name: 'Templates with Your Photo', basic: false, local: true, party: true },
                { name: 'Video Design Templates', basic: false, local: true, party: true },
                { name: 'AI Video Generation', basic: false, local: true, party: true },
                { name: 'Premium Templates', basic: false, local: true, party: true },
                { name: 'Party-Specific Designs', basic: false, local: false, party: true },
                { name: 'Unlimited Downloads', basic: false, local: false, party: true },
                { name: 'Support', basic: 'Standard', local: 'VIP', party: 'Priority' }
              ].map((row, index) => (
                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#fcfcfc' } }}>
                  <TableCell component="th" scope="row" sx={{ color: '#475569', fontWeight: 500 }}>{row.name}</TableCell>
                  <TableCell align="center">
                    {typeof row.basic === 'boolean' ? (row.basic ? <CheckCircle sx={{ color: '#10b981' }} fontSize="small"/> : <Cancel sx={{ color: '#ef4444', opacity: 0.5 }} fontSize="small"/>) : <Typography variant="body2" fontWeight="bold">{row.basic}</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    {typeof row.local === 'boolean' ? (row.local ? <CheckCircle sx={{ color: '#10b981' }} fontSize="small"/> : <Cancel sx={{ color: '#ef4444', opacity: 0.5 }} fontSize="small"/>) : <Typography variant="body2" fontWeight="bold" color="#FF6A00">{row.local}</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    {typeof row.party === 'boolean' ? (row.party ? <CheckCircle sx={{ color: '#10b981' }} fontSize="small"/> : <Cancel sx={{ color: '#ef4444', opacity: 0.5 }} fontSize="small"/>) : <Typography variant="body2" fontWeight="bold" color="#dc2626">{row.party}</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* 🌟 FAQ SECTION 🌟 */}
      <Container maxWidth="md" sx={{ mb: 10 }}>
        <Box textAlign="center" mb={5}>
          <Typography variant="h4" fontWeight="900" color="#1E293B" mb={1}>Pricing FAQ</Typography>
        </Box>
        <Box>
          {[
            { q: "How does the profile system work?", a: "Plan 1 gives you 3 profiles (e.g., Hindi, English, and one other). You can also share these profiles with friends or family." },
            { q: "What's the benefit of 5 profiles in Plan 2?", a: "You get more languages, the ability to share with your team, and create different profiles for different political positions. Plus, you save with the annual discount." },
            { q: "What payment methods do you accept?", a: "We accept all major credit/debit cards, UPI, net banking, and popular digital wallets in India." },
            { q: "Can I change my plan anytime?", a: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately." }
          ].map((faq, index) => (
            <Accordion 
              key={index} 
              expanded={expandedFaq === `faq${index}`} 
              onChange={handleFaqChange(`faq${index}`)}
              elevation={0}
              sx={{ mb: 2, borderRadius: '12px !important', border: '1px solid rgba(0,0,0,0.05)', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#FF6A00' }} />} sx={{ p: 2, bgcolor: expandedFaq === `faq${index}` ? 'rgba(255,106,0,0.02)' : 'transparent' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LightbulbCircle sx={{ mr: 2, color: expandedFaq === `faq${index}` ? '#FF6A00' : '#CBD5E1' }} />
                  <Typography variant="subtitle1" fontWeight="700" color={expandedFaq === `faq${index}` ? '#1E293B' : '#475569'}>{faq.q}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                <Typography variant="body2" color="#64748B" lineHeight={1.6}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>

      {/* 🌟 BOTTOM CTA / TRUST INDICATORS 🌟 */}
      <Box sx={{ bgcolor: darkBg, py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" color="white" mb={6}>
            <Typography variant="h3" fontWeight="900" mb={2}>Ready to Get Started?</Typography>
            <Typography variant="subtitle1" color="#94A3B8" mb={4}>Join thousands of political leaders who trust PracharApp for their campaigns.</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button component={Link} to="/login" variant="contained" startIcon={<RocketLaunch />} sx={{ bgcolor: 'white', color: '#EE0979', px: 4, py: 1.5, borderRadius: '50px', fontWeight: 'bold' }}>
                Get Started
              </Button>
              <Button component="a" href="mailto:support@pracharapp.com" variant="outlined" startIcon={<Phone />} sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white', px: 4, py: 1.5, borderRadius: '50px', fontWeight: 'bold', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                Contact Us
              </Button>
            </Stack>
          </Box>
          
          <Grid container spacing={4} justifyContent="center" textAlign="center" color="white">
            <Grid item xs={12} md={4}>
              <People sx={{ fontSize: 40, color: '#FF6A00', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold">10,000+ Users</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Star sx={{ fontSize: 40, color: '#f59e0b', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold">4.8 Rating</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Security sx={{ fontSize: 40, color: '#10b981', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold">Secure Payment</Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

    </Box>
  );
}